// ChatInterface.jsx - Complete Fixed Version with Textarea Focus Solutions

import { get as getCookie } from 'es-cookie'
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { Send, Eye, Code, Play, RotateCcw, AlertTriangle } from 'lucide-react'
import { Modal, withStyles } from '@material-ui/core'
import styles from './styles'
import Loading from './loading'
import { Icon } from '@iconify/react'
import CustomizedSwitch from 'src/components/customized-switch'
import { CoreButton } from 'src/components/core'
import {
  aiInteractionEnd,
  aiInteractionEndModeEdit,
} from 'src/components/editor/utils'
import { useDispatch, useSelector } from 'react-redux'
import MessageBoxContent, { MessageBoxType } from 'src/model/message-box'
import {
  messageBoxShowAction,
  messageBoxHideAction,
} from 'src/reducers/message-box/action'
import { ButtonActionType, ButtonData, ButtonType } from 'src/model/button'
import ChatTextarea from './chat-textarea'
import CodeTextarea from './code-textarea'
import { BuildProgressBubble, BuildProgressPanel } from './build-progress'
import {
  AI_GENERATION_URL,
  NEW_BUILD_EXPECTED_LENGTH,
  createBuildProgressTracker,
  createDoneBuildProgress,
  createInitialBuildProgress,
  fetchBuildResult,
  getStreamErrorMessage,
  useInteractionMetadata,
} from './build-progress/utils'
import { generateUUID } from 'src/components/editor/components/custom-plugins/simple-box/utils'
import { track } from '@amplitude/analytics-browser'
import { useCourseSettings } from 'src/hooks/useCourseSettings'

const ChatInterface = props => {
  const {
    classes,
    onStreamResponse,
    position,
    initialLoading = false,
    isModalOpen = false,
    handleCloseModal = () => {},
    onInteractionUsed = () => {},
    selectedData,
    selectedElementData = null,
    mode = 'normal',
    onSendMessage,
    streamSource = 'ChatInterface',
    sessionId = null,
  } = props

  const getInitialMessages = () => {
    if (mode === 'editInteraction' && selectedElementData) {
      return [
        {
          id: 0,
          text:
            '✏️ You are now editing the interactive element.\n\nJust describe what you want to modify and let the AI make the changes.',
          isBot: true,
          timestamp: new Date(),
        },
      ]
    }

    const messageText =
      selectedData?.text || selectedData?.description || 'No content provided'

    return [
      {
        id: 0,
        text: messageText,
        isBot: false,
        timestamp: new Date(),
      },
    ]
  }

  const [messages, setMessages] = useState(getInitialMessages())
  const { value: languageValue } = useCourseSettings('language')
  const courseLanguage = languageValue?.selected
  const [inputValue, setInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isStreaming, setIsStreaming] = useState(false)
  const [currentStreamMessage, setCurrentStreamMessage] = useState('')
  const [currentMessageId, setCurrentMessageId] = useState(null)
  const [htmlPreview, setHtmlPreview] = useState('')
  const [editableHtmlCode, setEditableHtmlCode] = useState('')
  const [zIndex, setZIndex] = useState('1360')
  const [showCode, setShowCode] = useState(false)
  const [showPreviewPanel, setShowPreviewPanel] = useState(true)
  const [codeChanged, setCodeChanged] = useState(false)
  const [messageStarted, setMessageStarted] = useState(false)
  const [isBuilding, setIsBuilding] = useState(false)
  // Set while a full build runs; the streamed HTML is never rendered meanwhile
  const [buildProgress, setBuildProgress] = useState(null)

  const [hasLastWorkingVersion, setHasLastWorkingVersion] = useState(false)
  const [showErrorRecovery, setShowErrorRecovery] = useState(false)
  const [lastError, setLastError] = useState(null)

  const [updateType, setUpdateType] = useState(null)
  const [isIncrementalUpdate, setIsIncrementalUpdate] = useState(false)
  const [targetLines, setTargetLines] = useState([])
  const [htmlLines, setHtmlLines] = useState([])

  const messagesEndRef = useRef(null)
  const iframeRef = useRef(null)
  const codeTextAreaRef = useRef(null)
  const textareaRef = useRef(null)
  // Accumulated stream text, kept in a ref so side effects stay out of state updaters
  const streamBufferRef = useRef('')
  // Used when no session is passed in (e.g. editing an existing interaction)
  const ownSessionIdRef = useRef(generateUUID())
  const getInteractionMetadata = useInteractionMetadata()
  const dispatch = useDispatch()

  const getStorageKey = () => {
    const baseKey =
      mode === 'editInteraction'
        ? `ai_interaction_edit_${selectedElementData?.blockId || 'unknown'}`
        : `ai_interaction_${Date.now()}`
    return baseKey
  }

  const saveHtmlToStorage = html => {
    if (html && html.trim()) {
      try {
        const storageData = {
          html: html,
          timestamp: new Date().toISOString(),
          mode: mode,
          elementData: selectedElementData,
        }
        localStorage.setItem(getStorageKey(), JSON.stringify(storageData))
        setHasLastWorkingVersion(true)
      } catch (error) {
        console.error('Failed to save HTML to localStorage:', error)
      }
    }
  }

  const loadHtmlFromStorage = () => {
    try {
      const stored = localStorage.getItem(getStorageKey())
      if (stored) {
        const data = JSON.parse(stored)
        return data.html
      }
    } catch (error) {
      console.error('Failed to load HTML from localStorage:', error)
    }
    return null
  }

  const checkForWorkingVersion = () => {
    const stored = loadHtmlFromStorage()
    setHasLastWorkingVersion(!!stored)
  }

  const revertToLastWorkingVersion = () => {
    const lastWorkingHtml = loadHtmlFromStorage()
    if (lastWorkingHtml) {
      setHtmlPreview(lastWorkingHtml)
      setEditableHtmlCode(lastWorkingHtml)
      updateHtmlLines(lastWorkingHtml)
      setShowErrorRecovery(false)
      setLastError(null)

      const recoveryMessage = {
        id: Date.now(),
        text:
          '🔄 Reverted to last working version successfully!\n\nYour interactive content has been restored to the previous state.',
        isBot: true,
        timestamp: new Date(),
        isSystemMessage: true,
      }
      setMessages(prev => [...prev, recoveryMessage])
    }
  }

  const showRecoveryConfirmation = () => {
    setZIndex('1298')
    dispatch(
      messageBoxShowAction(
        MessageBoxContent({
          title: `<div style="display: flex; align-items: center; justify-content: center; gap: 8px; color: var(--mcg-dark, #002E47); font-family: Inter; font-size: 18px; font-weight: 600; line-height: 28px;">
                <Typography>Revert to Last Working Version</Typography>
              </div>`,
          type: MessageBoxType.WARNING,
          message: 'Do you want to revert to the last working version?',
          buttons: [
            new ButtonData(
              ButtonType.NORMAL,
              'Cancel',
              ButtonActionType.NO,
              () => {
                dispatch(messageBoxHideAction())
              },
            ),
            new ButtonData(
              ButtonType.ACTION,
              'Yes, revert',
              ButtonActionType.YES,
              () => {
                dispatch(messageBoxHideAction())
                revertToLastWorkingVersion()
              },
            ),
          ],
          component: (
            <div
              style={{
                gap: '8px',
                color: 'var(--mcg-dark, #002E47)',
                fontFamily: 'Inter',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '22px',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <p className={classes.title}>
                This will replace the current content with the last working
                version that was successfully generated.
              </p>
              <p className={classes.closeTitle}>
                Are you sure you want to continue?
              </p>
            </div>
          ),
          callback: () => {
            setZIndex('1360')
          },
          maxWidth: '500px',
        }),
      ),
    )
  }

  const updateHtmlLines = newHtml => {
    const lines = newHtml.split('\n')
    setHtmlLines(lines)
    setHtmlPreview(newHtml)
    setEditableHtmlCode(newHtml)
  }

  const applyIncrementalUpdate = (fragmentData, targetLineNumbers) => {
    const currentLines =
      htmlLines.length > 0 ? [...htmlLines] : htmlPreview.split('\n')
    let updatedLines = [...currentLines]
    let changesApplied = 0

    const fragmentLines = fragmentData.trim().split('\n')
    const updates = {}

    fragmentLines.forEach(line => {
      const match = line.match(/^(\d+):\s*(.*)$/)
      if (match) {
        const lineNumber = parseInt(match[1])
        const content = match[2]
        updates[lineNumber] = content
      }
    })

    Object.keys(updates).forEach(lineNumber => {
      const lineIndex = parseInt(lineNumber)
      if (lineIndex >= 0 && lineIndex < updatedLines.length) {
        const oldContent = updatedLines[lineIndex]
        const newContent = updates[lineNumber]

        if (oldContent !== newContent) {
          updatedLines[lineIndex] = newContent
          changesApplied++
        }
      }
    })

    const updatedHtml = updatedLines.join('\n')
    updateHtmlLines(updatedHtml)
    saveHtmlToStorage(updatedHtml)

    const successMessage = `✅ Applied ${changesApplied} line changes`
    setCurrentStreamMessage(prev => prev + `\n\n${successMessage}`)
  }

  // FIXED: Improved initialLoading management
  useEffect(() => {
    // Edit mode'da hiç loading gösterme, diğer durumlarda da hızlıca kapat
    if (mode === 'editInteraction') {
      setIsLoading(false)
    } else if (initialLoading) {
      // Çok kısa bir loading süre sonra kapat
      setIsLoading(true)
      const timer = setTimeout(() => {
        setIsLoading(false)
      }, 50) // Minimize loading time
      return () => clearTimeout(timer)
    } else {
      setIsLoading(false)
    }
  }, [initialLoading, mode])

  useEffect(() => {
    checkForWorkingVersion()
  }, [])

  // Focus management - ONLY on initial modal open, never again
  const hasInitiallyFocused = useRef(false)

  useEffect(() => {
    if (isModalOpen && !hasInitiallyFocused.current) {
      // Single focus attempt when modal first opens - ONLY ONCE
      const timer = setTimeout(() => {
        const activeEl = document.activeElement
        if (
          textareaRef.current &&
          !textareaRef.current.disabled &&
          (!activeEl || activeEl === document.body)
        ) {
          textareaRef.current?.focus()
          hasInitiallyFocused.current = true
        }
      }, 150)

      return () => clearTimeout(timer)
    }

    // Reset when modal closes
    if (!isModalOpen) {
      hasInitiallyFocused.current = false
      ownSessionIdRef.current = generateUUID()
    }
  }, [isModalOpen])

  useEffect(() => {
    if (mode === 'editInteraction' && selectedElementData) {
      setMessages(getInitialMessages())

      if (selectedElementData.existingHtml) {
        setHtmlPreview(selectedElementData.existingHtml)
        setEditableHtmlCode(selectedElementData.existingHtml)
        updateHtmlLines(selectedElementData.existingHtml)
        setShowPreviewPanel(true)
        setShowCode(false)
        saveHtmlToStorage(selectedElementData.existingHtml)
      }

      setUpdateType(null)
      setIsIncrementalUpdate(false)
      setTargetLines([])
    } else {
      setMessages(getInitialMessages())
    }
  }, [mode, selectedElementData, selectedData])

  // Debug useEffect - can be removed after fixing

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const scrollCodeToBottom = () => {
    if (codeTextAreaRef.current?.current) {
      codeTextAreaRef.current.scrollTop = codeTextAreaRef.current.scrollHeight
    }
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages, currentStreamMessage])

  useEffect(() => {
    if (showCode && editableHtmlCode && isStreaming) {
      scrollCodeToBottom()
    }
  }, [editableHtmlCode, showCode, isStreaming])

  const extractHtmlContent = text => {
    if (!text) return null

    const htmlMatches = text.match(/```html\n([\s\S]*?)\n```/g)
    if (htmlMatches && htmlMatches.length > 0) {
      let htmlContent = htmlMatches[htmlMatches.length - 1]
      htmlContent = htmlContent.replace(/```html\n/, '').replace(/\n```$/, '')
      return htmlContent.trim()
    }

    const fullHtmlMatch = text.match(/<!DOCTYPE html[\s\S]*?<\/html>/gi)
    if (fullHtmlMatch && fullHtmlMatch.length > 0) {
      return fullHtmlMatch[fullHtmlMatch.length - 1].trim()
    }

    const htmlTagMatch = text.match(/<html[\s\S]*?<\/html>/gi)
    if (htmlTagMatch && htmlTagMatch.length > 0) {
      return htmlTagMatch[htmlTagMatch.length - 1].trim()
    }

    if (
      text.includes('<div') ||
      text.includes('<html') ||
      text.includes('<body')
    ) {
      return text.trim()
    }

    return null
  }

  // Code management
  const applyCodeChanges = () => {
    if (editableHtmlCode.trim()) {
      setHtmlPreview(editableHtmlCode)
      updateHtmlLines(editableHtmlCode)
      setCodeChanged(false)
      setShowCode(false)
      saveHtmlToStorage(editableHtmlCode)
    }
  }

  const handleCodeChange = newCode => {
    setEditableHtmlCode(newCode)
    setCodeChanged(true)
  }

  useEffect(() => {
    if (codeChanged && editableHtmlCode.trim()) {
      const timer = setTimeout(() => {
        setHtmlPreview(editableHtmlCode)
        updateHtmlLines(editableHtmlCode)
        saveHtmlToStorage(editableHtmlCode)
      }, 1000)

      return () => clearTimeout(timer)
    }
  }, [editableHtmlCode, codeChanged])

  // Stream management
  const addMessage = useCallback(message => {
    setMessages(prev => [...prev, message])
  }, [])

  const startStreamResponse = useCallback(
    messageId => {
      setIsStreaming(true)
      setCurrentMessageId(messageId)
      setCurrentStreamMessage('')
      streamBufferRef.current = ''

      setCodeChanged(false)
      setShowCode(false)
      setIsBuilding(false)
      setUpdateType(null)
      setIsIncrementalUpdate(false)
      setTargetLines([])

      setShowPreviewPanel(true)
      setIsLoading(true)
      setMessageStarted(false)
      setShowErrorRecovery(false)
      setLastError(null)
    },
    [],
  )

  const closeModal = () => {
    if (document.getElementById('message-box-0')) {
      document.getElementById('message-box-0').style.display = 'none'
    }

    if (mode === 'editInteraction' && selectedElementData) {
      const currentHtml = htmlPreview || editableHtmlCode
      const originalHtml = selectedElementData.existingHtml || ''

      if (currentHtml.trim() === originalHtml.trim()) {
        handleCloseModal()
        return
      }
    }

    setZIndex('1298')
    dispatch(
      messageBoxShowAction(
        MessageBoxContent({
          title: `<div style="display: flex; align-items: center; justify-content: center; gap: 8px; color: var(--mcg-dark, #002E47); font-family: Inter; font-size: 18px; font-weight: 600; line-height: 28px;">
              <Typography>Unsaved Changes</Typography>
            </div>`,
          type: MessageBoxType.WARNING,
          message:
            'You have unsaved changes. Do you want to leave without saving?',
          buttons: [
            new ButtonData(
              ButtonType.NORMAL,
              'Discard changes',
              ButtonActionType.NO,
              () => {
                setZIndex('1360')
                dispatch(messageBoxHideAction())
                handleCloseModal()
              },
            ),
            new ButtonData(
              ButtonType.ACTION,
              'Save and close editor',
              ButtonActionType.YES,
              () => {
                setZIndex('1360')
                const currentHtml = htmlPreview || editableHtmlCode
                if (currentHtml && currentHtml.trim() !== '') {
                  if (mode === 'editInteraction') {
                    const elementToEdit = selectedElementData?.element
                    const elementPosition = selectedElementData?.position

                    if (elementToEdit && window.editor) {
                      aiInteractionEndModeEdit(
                        window.editor,
                        currentHtml,
                        elementPosition,
                        elementToEdit,
                      )
                    } else {
                      aiInteractionEndModeEdit(
                        window.editor,
                        currentHtml,
                        window.editor.model.document.selection.getFirstPosition(),
                      )
                    }
                  } else {
                    aiInteractionEnd(window.editor, currentHtml, position)
                  }

                  onInteractionUsed()
                }
                dispatch(messageBoxHideAction())
                handleCloseModal()
              },
            ),
          ],
          component: (
            <div
              style={{
                gap: '8px',
                color: 'var(--mcg-dark, #002E47)',
                fontFamily: 'Inter',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '22px',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <p className={classes.title}>
                You have unsaved changes. If you leave the editor now, your
                changes will be lost.
              </p>
              <p className={classes.closeTitle}>What would you like to do? </p>
            </div>
          ),
          callback: () => {
            setZIndex('1360')
          },
          maxWidth: '500px',
        }),
      ),
    )
  }

  // MODIFIED: InteractionBuilder için özel updateStreamMessage
  const updateStreamMessage = useCallback(
    chunk => {
      // Run side effects outside the state updater so they apply in call order;
      // otherwise the last chunk's setShowCode(true) can land after endStreamResponse
      streamBufferRef.current += chunk
      setCurrentStreamMessage(prevMessage => prevMessage + chunk)

      const extractedHtml = extractHtmlContent(streamBufferRef.current)
      if (extractedHtml && extractedHtml !== htmlPreview) {
        setHtmlPreview(extractedHtml)
        setEditableHtmlCode(extractedHtml)
        updateHtmlLines(extractedHtml)
        setCodeChanged(false)

        if (streamSource === 'InteractionBuilder') {
          setShowCode(true)
          setIsBuilding(false)
        } else {
          if (!showCode) {
            setShowCode(true)
            setIsBuilding(true)
          }
        }
      }
    },
    [htmlPreview, streamSource, showCode],
  )

  const endStreamResponse = useCallback(() => {
    setIsStreaming(false)
    setCurrentStreamMessage('')
    streamBufferRef.current = ''
    setCurrentMessageId(null)
    setIsLoading(false)
    setMessageStarted(false)

    if (streamSource === 'InteractionBuilder') {
      setShowCode(false)
      setIsBuilding(false)
    } else {
      setShowCode(false)
      setIsBuilding(false)
      setUpdateType(null)
      setIsIncrementalUpdate(false)
      setTargetLines([])
    }
  }, [streamSource])

  // Build progress handlers used by InteractionBuilder for the first build
  const startBuild = useCallback(() => {
    setIsStreaming(true)
    setIsLoading(true)
    setShowCode(false)
    setCodeChanged(false)
    setShowPreviewPanel(true)
    setShowErrorRecovery(false)
    setLastError(null)
    setBuildProgress(createInitialBuildProgress())
  }, [])

  const finishBuild = useCallback(() => {
    setIsLoading(true)
    setBuildProgress(createDoneBuildProgress())
  }, [])

  const stopBuild = useCallback(() => {
    setBuildProgress(null)
    setIsStreaming(false)
    setIsLoading(false)
  }, [])

  const showBuildResult = useCallback(
    html => {
      updateHtmlLines(html)
      setCodeChanged(false)
      stopBuild()
    },
    [stopBuild],
  )

  const runRetry = messageId => {
    const retryMessage = messages.find(msg => msg.id === messageId)
    setMessages(prev =>
      prev.map(msg => (msg.id === messageId ? { ...msg, onRetry: null } : msg)),
    )
    retryMessage?.onRetry?.()
  }

  // Enhanced error handling function
  const handleAPIError = (error, botMessageId, onRetry = null) => {
    setBuildProgress(null)
    setLastError(error)
    setShowErrorRecovery(hasLastWorkingVersion)

    let errorMessage = `❌ **Error occurred during generation**\n\n`

    if (
      error.message.includes('overloaded') ||
      error.message.includes('Overloaded')
    ) {
      errorMessage += `The AI service is currently overloaded. Please try again in a moment.\n\n`
    } else {
      errorMessage += `${error.message}\n\n`
    }

    if (hasLastWorkingVersion) {
      errorMessage += `💡 **Good news**: I have your last working version saved. You can restore it using the "Revert to Last Working Version" button above.`
    } else {
      errorMessage += `Please try rephrasing your request or try again later.`
    }

    setMessages(prev =>
      prev.map(msg =>
        msg.id === botMessageId
          ? {
              ...msg,
              text: errorMessage,
              isStreaming: false,
              isError: true,
              onRetry,
            }
          : msg,
      ),
    )

    setIsLoading(false)
    setIsStreaming(false)
    setMessageStarted(false)

    if (streamSource === 'InteractionBuilder') {
      setIsBuilding(false)
    } else {
      setShowCode(false)
      setIsBuilding(false)
      setUpdateType(null)
      setIsIncrementalUpdate(false)
      setTargetLines([])
    }
  }

  // Smart HTML Chat API - FIXED with Incremental Update Parser
  const callSmartHtmlChatAPI = async (message, botMessageId) => {
    try {
      const endpoint = `${AI_GENERATION_URL}/api/htmlChatSmartSonnet55`
      const buildId = generateUUID()

      const requestBody = {
        html:
          htmlPreview ||
          editableHtmlCode ||
          selectedElementData?.existingHtml ||
          '',
        message: message,
        language: courseLanguage,
        mode: mode,
        build_id: buildId,
        metadata: getInteractionMetadata(sessionId || ownSessionIdRef.current),
      }

      if (mode === 'editInteraction' && selectedElementData) {
        requestBody.elementData = selectedElementData
        requestBody.existingHtml = selectedElementData.existingHtml || ''
        requestBody.interactionCode = selectedElementData.interactionCode || ''
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getCookie('token')}`,
        },
        body: JSON.stringify(requestBody),
      })

      if (!response.ok) {
        const errorData = await response.text()
        let parsedError
        try {
          parsedError = JSON.parse(errorData)
        } catch {
          parsedError = { message: `HTTP ${response.status}: ${errorData}` }
        }

        if (
          parsedError.type === 'overloaded_error' ||
          response.status === 503
        ) {
          throw new Error(
            'Service is currently overloaded. Please try again in a few moments.',
          )
        }

        throw new Error(
          parsedError.message || `HTTP error! status: ${response.status}`,
        )
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()

      let fullResponse = ''
      let htmlContent = ''
      let messageContent = ''

      setShowCode(false)
      setMessageStarted(false)
      setIsBuilding(false)

      let incrementalUpdateProcessed = false
      // A rewrite of the whole interaction shows build progress; SIMPLE changes keep "Thinking..."
      let isFullBuild = false
      const progressTracker = createBuildProgressTracker(
        requestBody.html.length || NEW_BUILD_EXPECTED_LENGTH,
      )

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const chunk = decoder.decode(value, { stream: true })
        fullResponse += chunk
        const progress = progressTracker.push(chunk)

        if (!isFullBuild) {
          const changeType = fullResponse.match(/##change_type##(\w+)/)?.[1]
          isFullBuild =
            changeType !== 'SIMPLE' &&
            (!!changeType || fullResponse.includes('##html_start##'))
        }
        if (isFullBuild) {
          setBuildProgress(progress)
        }

        if (
          fullResponse.includes('##change_type##') &&
          !incrementalUpdateProcessed
        ) {
          const changeTypeMatch = fullResponse.match(/##change_type##(\w+)/)
          if (changeTypeMatch) {
            const updateType = changeTypeMatch[1]
            setUpdateType(updateType)

            if (updateType === 'SIMPLE') {
              setIsIncrementalUpdate(true)

              const targetLinesMatch = fullResponse.match(
                /##target_lines##\s*\[([^\]]+)\]/,
              )
              if (targetLinesMatch) {
                const lineNumbers = targetLinesMatch[1]
                  .split(',')
                  .map(n => parseInt(n.trim()))
                setTargetLines(lineNumbers)
              }

              const incrementalMatch = fullResponse.match(
                /##incremental_start##\s*##html_fragment##\s*([\s\S]*?)\s*##incremental_end##/,
              )
              if (incrementalMatch) {
                const fragmentData = incrementalMatch[1].trim()

                if (fragmentData) {
                  applyIncrementalUpdate(fragmentData, targetLines)
                  setIsBuilding(false)
                  incrementalUpdateProcessed = true
                }
              }
            }
          }
        }

        // Partial HTML is never rendered; it is only read once the section is complete
        if (!htmlContent) {
          const htmlStartIndex = fullResponse.indexOf('##html_start##')
          const htmlEndIndex = fullResponse.indexOf('##html_end##')
          if (htmlStartIndex !== -1 && htmlEndIndex !== -1) {
            htmlContent = fullResponse
              .substring(htmlStartIndex + 14, htmlEndIndex)
              .trim()
          }
        }

        if (
          fullResponse.includes('##message_start##') &&
          fullResponse.includes('##message_end##')
        ) {
          const messageStartIndex = fullResponse.indexOf('##message_start##')
          const messageEndIndex = fullResponse.indexOf('##message_end##')

          if (messageStartIndex !== -1 && messageEndIndex !== -1) {
            const extractedMessage = fullResponse
              .substring(messageStartIndex + 17, messageEndIndex)
              .trim()

            if (extractedMessage && extractedMessage !== messageContent) {
              messageContent = extractedMessage
              if (!isFullBuild) {
                setCurrentStreamMessage(extractedMessage)
                setMessageStarted(true)
              }
            }
          }
        }
      }

      const streamError = getStreamErrorMessage(fullResponse)
      if (streamError) {
        throw new Error(streamError)
      }

      if (isFullBuild) {
        await loadChatBuildResult(buildId, botMessageId, messageContent)
        return
      }

      // Final cleanup
      if (htmlContent && !incrementalUpdateProcessed) {
        setHtmlPreview(htmlContent)
        setEditableHtmlCode(htmlContent)
        updateHtmlLines(htmlContent)
        saveHtmlToStorage(htmlContent)
        setShowCode(false)
      }

      if (incrementalUpdateProcessed) {
        setShowCode(false)
      }

      completeChatResponse(botMessageId, messageContent)
    } catch (error) {
      handleAPIError(error, botMessageId)
    }
  }

  // Loads the finished build by id; the streamed HTML itself is never rendered
  const loadChatBuildResult = async (buildId, botMessageId, messageContent) => {
    setIsLoading(true)
    setBuildProgress(createDoneBuildProgress())
    try {
      const html = await fetchBuildResult(buildId)
      updateHtmlLines(html)
      saveHtmlToStorage(html)
      setCodeChanged(false)
      setShowCode(false)
      setBuildProgress(null)
      completeChatResponse(botMessageId, messageContent)
    } catch (error) {
      handleAPIError(error, botMessageId, () =>
        loadChatBuildResult(buildId, botMessageId, messageContent),
      )
    }
  }

  const completeChatResponse = (botMessageId, messageContent) => {
    if (messageContent) {
      setMessages(prev =>
        prev.map(msg =>
          msg.id === botMessageId
            ? {
                ...msg,
                text: messageContent,
                isStreaming: false,
                isError: false,
              }
            : msg,
        ),
      )
    }

    setIsStreaming(false)
    setCurrentStreamMessage('')
    setCurrentMessageId(null)
    setIsLoading(false)
    setMessageStarted(false)
    setShowErrorRecovery(false)
    setLastError(null)
    setIsBuilding(false)

    setUpdateType(null)
    setIsIncrementalUpdate(false)
    setTargetLines([])
  }

  const renderMessage = message => {
    return (
      <div
        key={message.id}
        className={
          message.isBot ? classes.botMessageWrapper : classes.userMessageWrapper
        }
      >
        {message.isBot ? (
          <div className={classes.messageText}>
            {message.updateType && (
              <div>
                {message.targetLines && message.targetLines.length > 0 && (
                  <span style={{ marginLeft: '8px', opacity: 0.8 }}>
                    • {message.targetLines.length} line(s)
                  </span>
                )}
              </div>
            )}

            {message.id === currentMessageId && isStreaming
              ? currentStreamMessage
              : message.text}

            {message.id === currentMessageId && isStreaming && (
              <span className={classes.streamingCursor}>|</span>
            )}

            {message.onRetry && (
              <div className={classes.retryRow}>
                <CoreButton
                  startIcon={<RotateCcw size={14} />}
                  color="secondary"
                  variant="outlined"
                  size="small"
                  onClick={() => runRetry(message.id)}
                  disabled={isLoading || isStreaming}
                >
                  Retry
                </CoreButton>
              </div>
            )}
          </div>
        ) : (
          <div className={classes.userMessage}>
            <span
              className={classes.messageText}
              style={{ whiteSpace: 'pre-wrap' }}
            >
              {message.text}
            </span>
          </div>
        )}
      </div>
    )
  }

  // FIXED: Optimized input handling
  const handleInputChange = useCallback(e => {
    setInputValue(e.target.value)
  }, [])

  const handleSendMessage = useCallback(async () => {
    track('interactive_activity_message_sent')
    if (inputValue.trim() === '' || isLoading || isStreaming) return

    const userMessage = {
      id: Date.now(),
      text: inputValue,
      isBot: false,
      timestamp: new Date(),
    }

    const botMessageId = Date.now() + 1
    const botMessage = {
      id: botMessageId,
      text: '',
      isBot: true,
      timestamp: new Date(),
      isStreaming: true,
    }

    setMessages(prev => [...prev, userMessage, botMessage])
    const messageToSend = inputValue
    setInputValue('')
    setIsLoading(true)

    startStreamResponse(botMessageId)

    if (onSendMessage) {
      onSendMessage(messageToSend, botMessageId)
    }

    await callSmartHtmlChatAPI(messageToSend, botMessageId)
  }, [inputValue, isLoading, isStreaming, onSendMessage, startStreamResponse])

  // FIXED: Optimized key press handling
  const handleKeyPress = useCallback(
    e => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSendMessage()
      }
    },
    [handleSendMessage],
  )

  useEffect(() => {
    if (onStreamResponse) {
      onStreamResponse({
        startStream: startStreamResponse,
        updateStream: updateStreamMessage,
        endStream: endStreamResponse,
        addMessage: addMessage,
        startBuild,
        updateBuild: setBuildProgress,
        finishBuild,
        showBuildResult,
        stopBuild,
      })
    }
  }, [
    onStreamResponse,
    startStreamResponse,
    updateStreamMessage,
    endStreamResponse,
    addMessage,
    startBuild,
    finishBuild,
    showBuildResult,
    stopBuild,
  ])

  useEffect(() => {
    const handleBeforeUnload = e => {
      if (!isModalOpen) return

      const currentHtml = htmlPreview || editableHtmlCode
      if (currentHtml && currentHtml.trim() !== '') {
        if (mode === 'editInteraction' && selectedElementData) {
          const originalHtml = selectedElementData.existingHtml || ''
          if (currentHtml.trim() !== originalHtml.trim()) {
            e.preventDefault()
            e.returnValue =
              'You have unsaved changes. Are you sure you want to leave?'
            return e.returnValue
          }
        } else {
          e.preventDefault()
          e.returnValue =
            'You have unsaved changes. Are you sure you want to leave?'
          return e.returnValue
        }
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [isModalOpen, htmlPreview, editableHtmlCode, mode, selectedElementData])

  return (
    <Modal
      id="message-box-2"
      title=""
      open={isModalOpen}
      onClose={() => {}}
      disableBackdropClick={true}
      disableEnforceFocus={true}
      disableAutoFocus={true}
      style={{
        zIndex: zIndex,
      }}
      BackdropProps={{
        style: {
          backgroundColor: 'transparent',
        },
      }}
      onEntered={() => {
        // Focus is handled by useEffect, no need to do it here
      }}
    >
      <div
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: '#fff',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '8px',
          overflow: 'hidden',
          outline: 'none',
          backgroundColor: '#F4F6F8',
          gap: '20px',
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0 24px',
            background: '#fff',
            height: '56px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <CoreButton
              startIcon={<Icon icon="eva:close-outline" />}
              color="secondary"
              variant="text"
              size="small"
              onClick={closeModal}
            >
              {'Close Interaction Builder'}
            </CoreButton>

            {showErrorRecovery && hasLastWorkingVersion && (
              <CoreButton
                startIcon={<RotateCcw size={16} />}
                color="warning"
                variant="outlined"
                size="small"
                onClick={showRecoveryConfirmation}
                style={{
                  color: '#F59E0B',
                  borderColor: '#F59E0B',
                  backgroundColor: '#FEF3C7',
                }}
              >
                Revert to Last Working Version
              </CoreButton>
            )}
          </div>

          <CoreButton
            onClick={() => {
              const currentHtml = htmlPreview || editableHtmlCode
              if (currentHtml && currentHtml.trim() !== '') {
                if (mode === 'editInteraction') {
                  const elementToEdit = selectedElementData?.element
                  const elementPosition = selectedElementData?.position

                  if (elementToEdit && window.editor) {
                    aiInteractionEndModeEdit(
                      window.editor,
                      currentHtml,
                      elementPosition,
                      elementToEdit,
                    )
                  } else {
                    aiInteractionEndModeEdit(
                      window.editor,
                      currentHtml,
                      window.editor.model.document.selection.getFirstPosition(),
                    )
                  }
                } else {
                  aiInteractionEnd(window.editor, currentHtml, position)
                }

                handleCloseModal()
                onInteractionUsed()
              }
            }}
            size="small"
            color="secondary"
            variant="contained"
            disabled={
              (!htmlPreview && !editableHtmlCode) ||
              isLoading ||
              !!buildProgress
            }
          >
            {'Use this Interaction'}
          </CoreButton>
        </div>

        {/* ChatInterface Content */}
        <div className={classes.containerWithPreview} style={{ flex: 1 }}>
          <div
            className={classes.mainContent}
            style={{
              zIndex: zIndex,
            }}
          >
            <div className={classes.chatSection}>
              <div
                className={classes.messagesContainer}
                style={{
                  padding: '20px',
                  overflowY: 'auto',
                  flex: 1,
                  zIndex: zIndex,
                }}
              >
                {messages.map(message => {
                  if (
                    message.isBot &&
                    message.id === currentMessageId &&
                    isStreaming &&
                    !messageStarted
                  ) {
                    return null
                  }

                  return renderMessage(message)
                })}

                {buildProgress && (
                  <BuildProgressBubble progress={buildProgress} />
                )}

                {!buildProgress &&
                  (isLoading ||
                    (isStreaming &&
                      !currentStreamMessage &&
                      !messageStarted)) && (
                    <div className={classes.botMessageWrapper}>
                      <div className={classes.loadingContainer}>
                        <Loading isBuilding={isBuilding} />
                      </div>
                    </div>
                  )}

                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <div className={classes.inputArea}>
                <div className={classes.inputWrapper}>
                  <div className={classes.textareaWrapper}>
                    <ChatTextarea
                      ref={textareaRef}
                      value={inputValue}
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      placeholder={
                        mode === 'editInteraction'
                          ? 'What would you like me to change or improve?'
                          : 'Describe what you want to build...'
                      }
                      className={classes.textarea}
                      disabled={isLoading || isStreaming}
                      isLoading={isLoading}
                      isStreaming={isStreaming}
                      rows="1"
                    />
                    <button
                      onClick={handleSendMessage}
                      disabled={
                        inputValue.trim() === '' || isLoading || isStreaming
                      }
                      className={classes.sendButton}
                      style={{
                        background:
                          inputValue.trim() !== ''
                            ? 'var(--AI, linear-gradient(90deg, #50A7FF 0%, #5C68FA 78.4%))'
                            : 'var(--mcg-bg, #F4F6F8)',
                        color: inputValue.trim() !== '' ? '#fff' : '#919EAB',
                      }}
                    >
                      <Icon
                        size="small"
                        icon="iconamoon:send-light"
                        color={inputValue.trim() !== '' ? '#fff' : '#919EAB'}
                        width={18}
                      />
                    </button>
                  </div>
                </div>
                <span className={classes.warningText}>
                  AI Assistant can make mistakes. Check important info.
                </span>
              </div>
            </div>

            {showPreviewPanel && (
              <div className={classes.previewSection}>
                <div
                  className={classes.previewToggle}
                  style={buildProgress ? { opacity: 0.5 } : undefined}
                >
                  <CustomizedSwitch
                    checked={showCode}
                    onChange={() => setShowCode(!showCode)}
                    disabled={isLoading || isStreaming || !!buildProgress}
                  />
                  <span className={classes.toggleLabel}>{'Show code'}</span>
                  {buildProgress && (
                    <span className={classes.toggleHint}>
                      available when the build finishes
                    </span>
                  )}

                  {showCode && codeChanged && (
                    <button
                      onClick={applyCodeChanges}
                      className={classes.applyButton}
                      title="Apply changes and switch to preview"
                    >
                      <Play size={14} />
                      Apply Changes
                    </button>
                  )}
                </div>

                <div className={classes.previewContent}>
                  {buildProgress ? (
                    <BuildProgressPanel progress={buildProgress} />
                  ) : showCode ? (
                    <div className={classes.codeContainer}>
                      <CodeTextarea
                        ref={codeTextAreaRef}
                        className={`${classes.editableCodeArea} editableCodeArea`}
                        value={editableHtmlCode || htmlPreview}
                        onChange={e => handleCodeChange(e.target.value)}
                        placeholder=""
                        disabled={isLoading || isStreaming}
                        isLoading={isLoading}
                        isStreaming={isStreaming}
                      />

                      {codeChanged && (
                        <div className={classes.codeStatus}>
                          <span>
                            Code modified - changes will apply automatically in
                            1 second or click "Apply Changes"
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ height: '100%', position: 'relative' }}>
                      {htmlPreview || editableHtmlCode ? (
                        <iframe
                          ref={iframeRef}
                          className={classes.previewIframe}
                          title="HTML Preview"
                          sandbox="allow-scripts allow-same-origin allow-modals allow-forms"
                          srcDoc={htmlPreview || editableHtmlCode}
                          key={(htmlPreview || editableHtmlCode).substring(
                            0,
                            50,
                          )}
                        />
                      ) : (
                        <div style={{ width: '100%', height: '100%' }}>
                          {!isLoading && !isStreaming ? (
                            <div className={classes.noContentMessage}>
                              <div>No content to preview yet</div>
                              <div style={{ fontSize: '12px', opacity: 0.7 }}>
                                {mode === 'editInteraction'
                                  ? 'Send a message to modify the interactive content'
                                  : 'Send a message to generate interactive content'}
                              </div>
                            </div>
                          ) : (
                            <div className={classes.noContentMessage}>
                              <div>Generating interactive content...</div>
                              <div style={{ fontSize: '12px', opacity: 0.7 }}>
                                Please wait while the AI creates your content
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default withStyles(styles, { withTheme: true })(ChatInterface)
